(() => {
  const existing = window.MorrowValeQuickCart;
  if (existing) {
    existing.initAll(document);
    existing.initWishlist?.(document);
    return;
  }

  const controllers = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (section) => {
    controllers.get(section)?.destroy();

    const slider = section.querySelector('[data-qc-slider]');
    const track = slider?.querySelector('[data-qc-slider-track]');
    const slides = [...(slider?.querySelectorAll('[data-qc-slide]') || [])];
    const dots = [...(slider?.querySelectorAll('[data-qc-slide-dot]') || [])];
    if (!slider || !track || slides.length === 0) return;

    let index = 0;
    let intervalId = null;
    const pauseReasons = new Set();

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    };

    const stop = () => {
      if (intervalId !== null) window.clearInterval(intervalId);
      intervalId = null;
    };

    const start = () => {
      stop();
      if (pauseReasons.size === 0 && !reduceMotion.matches && !document.hidden && slides.length > 1) {
        intervalId = window.setInterval(() => show(index + 1), 5000);
      }
    };

    const addPauseReason = (reason) => {
      pauseReasons.add(reason);
      stop();
    };

    const removePauseReason = (reason) => {
      pauseReasons.delete(reason);
      start();
    };

    const onPointerEnter = () => addPauseReason('hover');
    const onPointerLeave = () => removePauseReason('hover');
    const onFocusIn = () => addPauseReason('focus');
    const onFocusOut = (event) => {
      if (!slider.contains(event.relatedTarget)) removePauseReason('focus');
    };

    const onKeydown = (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        if (event.key === 'ArrowLeft') show(index - 1);
        if (event.key === 'ArrowRight') show(index + 1);
        if (event.key === 'Home') show(0);
        if (event.key === 'End') show(slides.length - 1);
      }
    };

    const onVisibilityChange = () => start();
    const onMotionChange = () => start();
    const dotHandlers = dots.map((dot, dotIndex) => {
      const handler = () => show(dotIndex);
      dot.addEventListener('click', handler);
      return [dot, handler];
    });

    slider.addEventListener('pointerenter', onPointerEnter);
    slider.addEventListener('pointerleave', onPointerLeave);
    slider.addEventListener('focusin', onFocusIn);
    slider.addEventListener('focusout', onFocusOut);
    slider.addEventListener('keydown', onKeydown);
    document.addEventListener('visibilitychange', onVisibilityChange);
    reduceMotion.addEventListener('change', onMotionChange);

    const moreButton = section.querySelector('[data-qc-load-more]');
    const moreProducts = [...section.querySelectorAll('[data-more-product]')];
    const onMore = (event) => {
      event.preventDefault();
      const isExpanded = moreButton.getAttribute('aria-expanded') === 'true';
      moreProducts.forEach((card) => card.classList.toggle('is-visible', !isExpanded));
      moreButton.setAttribute('aria-expanded', String(!isExpanded));
      moreButton.textContent = isExpanded ? 'See more' : 'Show less';
    };
    if (moreButton && moreProducts.length) moreButton.addEventListener('click', onMore);

    show(0);
    start();
    section.setAttribute('data-qc-initialized', 'true');

    controllers.set(section, {
      destroy() {
        stop();
        pauseReasons.clear();
        dotHandlers.forEach(([dot, handler]) => dot.removeEventListener('click', handler));
        slider.removeEventListener('pointerenter', onPointerEnter);
        slider.removeEventListener('pointerleave', onPointerLeave);
        slider.removeEventListener('focusin', onFocusIn);
        slider.removeEventListener('focusout', onFocusOut);
        slider.removeEventListener('keydown', onKeydown);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        reduceMotion.removeEventListener('change', onMotionChange);
        if (moreButton && moreProducts.length) moreButton.removeEventListener('click', onMore);
        section.removeAttribute('data-qc-initialized');
        controllers.delete(section);
      }
    });
  };

  const initAll = (scope) => {
    if (scope.matches?.('[data-qc-home-section]')) init(scope);
    scope.querySelectorAll?.('[data-qc-home-section]').forEach(init);
  };

  const destroyAll = (scope) => {
    for (const [section, controller] of controllers) {
      if (section === scope || scope.contains?.(section)) controller.destroy();
    }
  };

  const wishlistStorageKey = 'morrow-vale:wishlist:v1';
  const savedProductKeys = new Set();
  let wishlistStorageMode = 'unknown';

  const replaceSavedProductKeys = (raw) => {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some((item) => typeof item !== 'string')) return false;
    savedProductKeys.clear();
    parsed.map((item) => item.trim()).filter(Boolean).forEach((item) => savedProductKeys.add(item));
    return true;
  };

  try {
    const stored = window.localStorage.getItem(wishlistStorageKey);
    if (stored === null || replaceSavedProductKeys(stored)) wishlistStorageMode = 'readable';
  } catch (_error) {
    // Unknown stored state must never be overwritten; this page stays memory-only.
  }

  const persistWishlist = () => {
    if (wishlistStorageMode !== 'readable' && wishlistStorageMode !== 'writable') return false;
    try {
      window.localStorage.setItem(wishlistStorageKey, JSON.stringify([...savedProductKeys].sort()));
      wishlistStorageMode = 'writable';
      return true;
    } catch (_error) {
      // Do not probe again in this document; a reload is the explicit recovery boundary.
      wishlistStorageMode = 'page';
      return false;
    }
  };

  const renderWishlistButton = (button) => {
    const productKey = button.dataset.productKey?.trim();
    if (!productKey) {
      button.hidden = true;
      return;
    }
    const productTitle = button.dataset.productTitle?.trim() || 'this product';
    const saved = savedProductKeys.has(productKey);
    const persistent = wishlistStorageMode === 'readable' || wishlistStorageMode === 'writable';
    button.hidden = false;
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved
      ? persistent
        ? `Remove ${productTitle} from saved items on this device`
        : `Remove ${productTitle} from saved items for this page`
      : persistent
        ? `Save ${productTitle} locally on this device`
        : `Save ${productTitle} for this page only`);
    const icon = button.querySelector('[data-qc-wishlist-icon]');
    if (icon) icon.textContent = saved ? '♥' : '♡';
    const label = button.querySelector('[data-qc-wishlist-label]');
    if (label) label.textContent = saved ? 'Saved' : 'Save for later';
  };

  const updateSavedChrome = () => {
    document.querySelectorAll('[data-qc-saved-count]').forEach((node) => {
      node.textContent = String(savedProductKeys.size);
    });
  };

  const initWishlist = (scope) => {
    if (scope.matches?.('[data-qc-wishlist]')) renderWishlistButton(scope);
    scope.querySelectorAll?.('[data-qc-wishlist]').forEach(renderWishlistButton);
    updateSavedChrome();
  };

  let wishlistLiveRegion = null;
  const announceWishlist = (message) => {
    if (!wishlistLiveRegion) {
      wishlistLiveRegion = document.createElement('span');
      wishlistLiveRegion.className = 'sr-only';
      wishlistLiveRegion.dataset.qcWishlistLive = '';
      wishlistLiveRegion.setAttribute('aria-live', 'polite');
      wishlistLiveRegion.setAttribute('aria-atomic', 'true');
      document.body.append(wishlistLiveRegion);
    }
    wishlistLiveRegion.textContent = message;
  };

  const savedShell = document.querySelector('[data-qc-saved-shell]');
  const savedList = savedShell?.querySelector('[data-qc-saved-list]');
  const savedEmpty = savedShell?.querySelector('[data-qc-saved-empty]');
  let savedReturnFocus = null;
  let savedRenderVersion = 0;

  const makeSavedItem = (productKey, product) => {
    const row = document.createElement('article');
    row.className = 'saved-item';

    const link = document.createElement('a');
    link.href = product?.url || `/products/${encodeURIComponent(productKey)}`;
    link.className = 'saved-item__link';
    if (product?.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image;
      image.alt = '';
      image.width = 96;
      image.height = 96;
      image.loading = 'lazy';
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'saved-item__placeholder';
      placeholder.textContent = productKey.slice(0, 1).toUpperCase();
      link.append(placeholder);
    }
    const title = document.createElement('strong');
    title.textContent = product?.title || productKey.replaceAll('-', ' ');
    link.append(title);
    row.append(link);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'saved-item__remove';
    remove.dataset.qcSavedRemove = productKey;
    remove.setAttribute('aria-label', `Remove ${title.textContent} from saved items`);
    remove.textContent = 'Remove';
    row.append(remove);
    return row;
  };

  const renderSavedDrawer = async () => {
    if (!savedList || !savedEmpty) return;
    const version = ++savedRenderVersion;
    const keys = [...savedProductKeys];
    savedList.replaceChildren();
    savedEmpty.hidden = keys.length > 0;
    if (!keys.length) return;
    savedList.setAttribute('aria-busy', 'true');
    const products = await Promise.all(keys.map(async (productKey) => {
      try {
        const response = await window.fetch(`/products/${encodeURIComponent(productKey)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (_error) {
        return null;
      }
    }));
    if (version !== savedRenderVersion) return;
    keys.forEach((productKey, index) => savedList.append(makeSavedItem(productKey, products[index])));
    savedList.removeAttribute('aria-busy');
  };

  const setSavedDrawerOpen = (open, trigger = null) => {
    if (!savedShell) return;
    document.querySelectorAll('[data-qc-saved-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
    if (open) {
      savedReturnFocus = trigger || document.activeElement;
      savedShell.hidden = false;
      document.documentElement.classList.add('saved-drawer-open');
      renderSavedDrawer();
      window.requestAnimationFrame(() => savedShell.querySelector('.saved-drawer')?.focus());
    } else {
      savedShell.hidden = true;
      document.documentElement.classList.remove('saved-drawer-open');
      savedReturnFocus?.focus?.();
      savedReturnFocus = null;
    }
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-localization-select]');
    if (select) select.form?.requestSubmit();
  });

  document.addEventListener('keydown', (event) => {
    if (!savedShell || savedShell.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setSavedDrawerOpen(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...savedShell.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((node) => !node.hidden && node.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      savedShell.querySelector('.saved-drawer')?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('click', (event) => {
    const savedToggle = event.target.closest?.('[data-qc-saved-toggle]');
    if (savedToggle) {
      event.preventDefault();
      setSavedDrawerOpen(true, savedToggle);
      return;
    }
    if (event.target.closest?.('[data-qc-saved-close]')) {
      event.preventDefault();
      setSavedDrawerOpen(false);
      return;
    }
    const removeKey = event.target.closest?.('[data-qc-saved-remove]')?.dataset.qcSavedRemove;
    if (removeKey) {
      savedProductKeys.delete(removeKey);
      const persisted = persistWishlist();
      initWishlist(document);
      renderSavedDrawer();
      announceWishlist(persisted ? 'Product removed from saved items on this device.' : 'Product removed from saved items for this page.');
      return;
    }
    const button = event.target.closest?.('[data-qc-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const productKey = button.dataset.productKey?.trim();
    if (!productKey) return;
    const productTitle = button.dataset.productTitle?.trim() || 'This product';
    const saved = !savedProductKeys.has(productKey);
    if (saved) savedProductKeys.add(productKey);
    else savedProductKeys.delete(productKey);
    const persisted = persistWishlist();
    initWishlist(document);
    if (savedShell && !savedShell.hidden) renderSavedDrawer();
    if (persisted) {
      announceWishlist(saved
        ? `${productTitle} saved locally on this device.`
        : `${productTitle} removed from saved items on this device.`);
    } else {
      announceWishlist(saved
        ? `${productTitle} saved for this page only; browser storage is unavailable.`
        : `${productTitle} removed from saved items for this page.`);
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== wishlistStorageKey || (wishlistStorageMode !== 'readable' && wishlistStorageMode !== 'writable')) return;
    if (event.newValue === null) {
      savedProductKeys.clear();
      initWishlist(document);
      if (savedShell && !savedShell.hidden) renderSavedDrawer();
      return;
    }
    try {
      if (replaceSavedProductKeys(event.newValue)) {
        initWishlist(document);
        if (savedShell && !savedShell.hidden) renderSavedDrawer();
      }
    } catch (_error) {
      // Ignore malformed writes from another context and preserve current state.
    }
  });

  window.MorrowValeQuickCart = { initAll, destroyAll, initWishlist };
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
    initWishlist(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  initAll(document);
  initWishlist(document);
})();
